import React, { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Button from '@jobcase/react_pattern_library/components/Button';
import ChevronLeftLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/ChevronLeftRebrandLightSVG';
import LinkRebrand, { PREV_LINK_STYLES } from '@jobcase/react_pattern_library/components/Link/LinkRebrand';
import ProfileTooltipMenu from '@jobcase/react_pattern_library/components/ProfileTooltipMenu/ProfileTooltipMenuRebrand';
import SVGIcon from '@jobcase/react_pattern_library/components/SVGIcon';
import SwitchAccountRebrandRegularSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/SwitchAccountRebrandRegularSVG';
import styles from '@jobcase/react_pattern_library/styles/rebrand';

import AmazonSpecificNavItems from '../AmazonSpecificNavItems/AmazonSpecificNavItemsRebrand';
import DesktopEmployerSwitcher from '../DesktopEmployerSwitcher/DesktopEmployerSwitcherRebrand';
import Logo from '../../../components/Logo/Logo';
import MobileEmployerSwitcher from '../MobileEmployerSwitcher/MobileEmployerSwitcher';
import { WithGrantsContext } from '../../../hoc/withGrants';

import { getCurrentEmployer, getCurrentSubEmployer } from '../../../redux/nodes/features/employers/selectors';
import { getCurrentProfile, getProfileKey } from '../../../redux/nodes/authentication/selectors';
import { getEmployerById } from '../../../redux/nodes/entities/employers/selectors';
import { getIsNavCollapsed } from '../../../redux/nodes/application/selectors';
import { getFilterOptions } from '../../../redux/nodes/features/jobs/selectors';
import { getFilterOptions as getJobGroupsFilterOptions } from '../../../redux/nodes/features/jobGroups/selectors';
import { getUnseenCount } from '../../../redux/nodes/entities/notifications/selectors';
import { routeToProfile } from '../../../redux/nodes/router/actions';
import { setCurrentEmployer } from '../../../redux/nodes/features/employers/actions';
import { setIsNavCollapsed } from '../../../redux/nodes/application/actions';
import { updateFilterOptions } from '../../../redux/nodes/features/jobs/actions';
import { updateFilterOptions as updateJobGroupsFilterOptions } from '../../../redux/nodes/features/jobGroups/actions';

import GrantsAPI from '../../../utils/api/grantsAPI';
import config from '../../../config';
import { fullName } from '../../../utils/profile';
import { getMenuItems } from '../../../utils/employerNavigationRebrand';
import { ALL_EMPLOYERS_ID, TRANSITION_DELAY } from '../../../utils/constants/employerNavigation';
import { useIsMobile } from '../../../utils/hooks';
import { EXPECTED_ROUTE_KEYS } from '../../../constants';
import { ALL_JOBS_QUERY_PARAMS } from '../../../utils/constants/dashboard';

const SecondaryNavigationContainer = styled.aside`
  background-color: ${styles.color.jobcaseWhiteColor};
  border-right: 1px solid ${styles.color.dividerGrayColor};
  height: 100vh;
  left: ${({ isOpen }) => (isOpen ? '0' : '-85%')};
  position: fixed;
  top: 0;
  transition: left ${TRANSITION_DELAY}s, width ${TRANSITION_DELAY}s;
  width: 85%;
  z-index: 1000;

  @media(min-width: ${styles.breakpoint.md}) {
    left: 0;
    transition: 0.3s cubic-bezier(0.39, 0.575, 0.565, 1);
    width: ${({ isSideBarCollapsed }) => (isSideBarCollapsed ? '56px' : '240px')};
  }

  .for-mobile {
    @media(min-width: ${styles.breakpoint.md}) {
      display: none;
    }
  }

  .for-desktop {
    display: none;

    @media(min-width: ${styles.breakpoint.md}) {
      display: block;
    }
  }
`;

const ProfileMenu = styled.div`
  bottom: ${styles.spacing.large}px;
  padding: 0 ${styles.spacing.extraTiny}px;
  left: 0;
  position: absolute;
  width: 100%;

  &:hover {
    background-color: ${styles.color.commentBoxGrayColor};
  }
`;

const ProfileTooltipMenuWrapper = styled.div`
  align-items: center;
  border-bottom: 1px solid ${styles.color.dividerGrayColor};
  display: flex;
  font-family: ${styles.font.fontFamily};
  font-size: ${styles.font.fontSize.body};
  padding: ${styles.spacing.extraTiny}px 0;
  width: 100%;

  @media (min-width: ${styles.breakpoint.md}) {
    [class*="ProfileTooltipMenuRebrand__StyledOuterTitleTypography"] {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  [class*="ProfileTooltipMenuRebrand__StyledOuterWrapper"] {
    flex-direction: row-reverse;
    width: 100%;
  }

  [class*="TooltipRebrand__TooltipText"] {
    border: 1px solid ${styles.color.dividerGrayColor};
    bottom: -${styles.spacing.extraTiny + 4}px;
    box-shadow: none;
    left: ${({ isSideBarCollapsed }) => (isSideBarCollapsed ? '56' : '240')}px;
    top: unset;
  }

  [class*="ProfileTooltipMenuRebrand__StyledTooltipWrapper"],
  [class*="TooltipRebrand__OuterWrapper"],
  [class*="TooltipRebrand__OuterWrapper"] > div,
  [class*="TooltipRebrand__TooltipContainer"] {
    width: 100%;
  }
`;

const SubNavLinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 0 ${styles.spacing.medium}px;
  position: relative;

  @media (min-width: ${styles.breakpoint.md}) {
    -ms-overflow-style: none;
    height: calc(100vh - 250px);
    scrollbar-width: none;

    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StyledLink = styled(LinkRebrand).attrs({ linkStyle: PREV_LINK_STYLES.NO_UNDERLINE })`
  align-items: center;
  border: none;
  display: flex;
  font-weight: ${({ bold }) => (bold ? '500' : '400')};
  max-height: 74px;
  min-height: 60px;
  min-width: 240px;
  padding: 0 ${styles.spacing.small + 2}px 0 ${styles.spacing.tiny}px;
  position: relative;
  text-decoration: none;
  transition: height ${TRANSITION_DELAY}s;

  ${({ isSelected }) => isSelected && css`
    background-color: ${styles.color.commentBoxGrayColor};

    &:before {
      background-color: ${styles.color.primaryColor};
    }
  `}

  &:before {
    bottom: 0;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 4px;
  }

  &:hover {
    background-color: ${styles.color.commentBoxGrayColor};

    ${({ isLabelHidden }) => !isLabelHidden && css`
      @media (min-width: ${styles.breakpoint.md}) {
        .nav-tooltip {
          background: ${styles.color.jobcaseBlackColor};
          border-radius: ${styles.spacing.borderRadius}px;
          color: ${styles.color.jobcaseWhiteColor}; 
          display: block;
          font-size: ${styles.font.fontSize.tertiary};
          left: 52px;
          line-height: ${styles.font.lineHeight.tertiary};
          padding: ${styles.spacing.extraExtraTiny}px ${styles.spacing.extraTiny}px;
          position: fixed;
        }
      }
    `}
  }

  @media (min-width: ${styles.breakpoint.md}) {
    padding: ${styles.spacing.tiny + 2}px ${styles.spacing.tiny}px;
  }
`;

const NavIcon = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-right: ${styles.spacing.tiny}px;

  svg {
    margin: 0;
    max-width: 24px;
    min-width: 24px;

    path {
      fill: ${styles.color.jobcaseBlackColor};
    }
  }
`;

const NavLink = styled.div`
  color: ${styles.color.jobcaseBlackColor};
  font-weight: 600;
  line-height: ${styles.font.lineHeight.body2};
`;

const NavTooltip = styled.span`
  display: none;
`;

const SideNavMask = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  height: 100%;
  left: 0;
  overflow-y: auto;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 100;

  @media (min-width: ${styles.breakpoint.md}) {
    display: none;
  }
`;

const Mask = styled.div`
  @media (min-width: ${styles.breakpoint.md}) {
    background-color: rgba(0, 0, 0, 0.5);
    height: 100%;
    left: 0;
    overflow-y: auto;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 100;
    transition: 0.2s ease-in-out;
    opacity: 0;
    visibility: hidden;

    ${({ isMaskDisplayed }) => (isMaskDisplayed && css`
      opacity: 1;
      visibility: visible ;
    `)}
  }
`;

const switchAccountIcon = styled(SwitchAccountRebrandRegularSVG)`
  height: ${styles.spacing.tiny}px;
  width: ${styles.spacing.tiny}px;
`;

const CollapseButton = styled(Button)`
  bottom: ${styles.spacing.extraTiny}px;
  left: ${({ isSideBarCollapsed }) => (isSideBarCollapsed ? '8px' : '192px')};
  min-width: 40px;
  padding: 0;
  position: fixed;
  transform: rotateZ(${({ isSideBarCollapsed }) => (isSideBarCollapsed ? '180deg' : '0')});
  transition: all ${TRANSITION_DELAY}s;

  button {
    align-items: center;
    background: ${styles.color.jobcaseWhiteColor};
    border-radius: 50%;
    border: 1px solid ${styles.color.dividerGrayColor};
    display: flex;
    height: 40px;
    justify-content: center;
    padding: 0;
    width: 40px;

    &:hover::after {
      background: transparent;
    }

    div {
      align-items: center;
      display: flex;
      justify-content: center;

      svg {
        height: 24px;
        width: 24px;
      }
    }
  }
`;

const AMAZON_SPECIFIC_EMPLOYER_IDS = [
  '1897bc5f-f610-40b5-a8cc-b58baffefdcc', // Amazon WFS
  '30733d0d-64db-46ef-89fe-14b9b350eb5d', // Amazon Retail
  'e77c99e5-b6ba-4a56-b30d-ae38ce0530bb', // Pillpack
];

const SecondaryNavigation = ({
  handleRoute,
  toggleOpen,
  onChange,
  setCurrentEmployerSelected,
  employers,
  currentEmployerSelected,
  menuItemSelected,
  showSidebar,
  isOpen,
}) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const router = useRouter();

  const {
    appendEmployerIdInRouteParams,
    isLegacyEmployer,
    allowedRoutes,
  } = useContext(WithGrantsContext);

  const currentEmployerId = useSelector(getCurrentEmployer);
  const currentSubEmployerId = useSelector(getCurrentSubEmployer);
  const currentEmployer = useSelector(getEmployerById(currentEmployerId));
  const currentProfile = useSelector(getCurrentProfile);
  const loggedInProfileKey = useSelector(getProfileKey);

  // Show a Partner entry point only for users who hold EC1 (Upward) roles. Their partner
  // reporting lives in EC1 (upward.net), not in Employer Center, so this is the bridge to it.
  // Read the current user's own upward grants directly; a non-empty roles list means partner.
  const [hasPartnerAccess, setHasPartnerAccess] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!loggedInProfileKey) return undefined;
    GrantsAPI()
      .getUpwardGrantsByProfile(loggedInProfileKey)
      .then((data) => {
        if (!cancelled) setHasPartnerAccess(!isEmpty(data?.roles));
      })
      .catch(() => {
        if (!cancelled) setHasPartnerAccess(false);
      });
    return () => { cancelled = true; };
  }, [loggedInProfileKey]);
  const filterJobGroupsOptions = useSelector(getJobGroupsFilterOptions);
  const filterJobsOptions = useSelector(getFilterOptions);
  const isSideBarCollapsed = useSelector(getIsNavCollapsed);
  const unseenNotificationCount = useSelector(getUnseenCount);

  const isAllEmployer = currentEmployerId === ALL_EMPLOYERS_ID;

  const [isLabelHidden, setLabelHidden] = useState(false);
  const [isMaskDisplayed, setMaskDisplayed] = useState(false);

  const MENU_ITEMS = getMenuItems(unseenNotificationCount);

  const toggleCollapse = () => {
    dispatch(setIsNavCollapsed(!isSideBarCollapsed));
  };

  const routesHandlerUnfiltered = isLegacyEmployer
    ? allowedRoutes.filter(route => ![
      EXPECTED_ROUTE_KEYS.SHOW_BILLING,
    ].includes(route?.key))
    : allowedRoutes;

  const routesHandler = isAllEmployer
    ? routesHandlerUnfiltered.filter(({ key }) => key === EXPECTED_ROUTE_KEYS.SHOW_USER_ROLES_PERMISSIONS)
    : routesHandlerUnfiltered;

  // TODO: Uncomment when ready to deploy beta pages
  // TODO: use `routesHandler` instead of `routesHandlerWithoutBetaPages` once Job Reporting and Manage Budgets pages are ready.
  const routesHandlerWithoutBetaPages = routesHandler.filter(item => item?.key !== EXPECTED_ROUTE_KEYS.SHOW_JOB_REPORTING
    && item?.key !== EXPECTED_ROUTE_KEYS.SHOW_MANAGE_BUDGETS);

  const handleOnClickViewProfile = () => {
    routeToProfile();
  };

  const clearJobsAndJobGroupsFilters = () => {
    dispatch(updateFilterOptions(omit(filterJobsOptions, ['job_group_id', 'sub_organization_id'])));
    dispatch(updateJobGroupsFilterOptions(omit(filterJobGroupsOptions, ['sub_organization_id'])));
  };

  const getResettedDashboardQueryParams = (employerId) => {
    const additionalQueryParams = {};

    if (employerId !== currentEmployerId) {
      const dashboardTableCurrentPageQueryParam = router.query?.[ALL_JOBS_QUERY_PARAMS.DASHBOARD_ALL_JOBS_CURRENT_PAGE];

      if (dashboardTableCurrentPageQueryParam && dashboardTableCurrentPageQueryParam !== '1') {
        additionalQueryParams[ALL_JOBS_QUERY_PARAMS.DASHBOARD_ALL_JOBS_CURRENT_PAGE] = '1';
      }
      if (router.query.jobIds) {
        additionalQueryParams.jobIds = [];
      }
      if (router.query.jobGroupIds) {
        additionalQueryParams.jobGroupIds = [];
      }
      if (router.query.subOrgIds) {
        additionalQueryParams.subOrgIds = [];
      }
    }

    return additionalQueryParams;
  };

  const desktopEmployerSwitcherOnChangeHandler = async (data) => {
    clearJobsAndJobGroupsFilters();
    const employerId = data[0]?.employer_id;
    const subEmployerId = data[0]?.sub_employer_id;
    const additionalQueryParams = getResettedDashboardQueryParams(employerId);
    dispatch(setCurrentEmployer(employerId, subEmployerId));
    appendEmployerIdInRouteParams(employerId, {
      ...additionalQueryParams,
      subEmployer: subEmployerId,
    });
  };

  const handleClose = () => {
    toggleOpen();
  };

  const handleMenuItemClick = (item) => {
    handleRoute(item, {
      employer: currentEmployer?.id,
      subEmployer: currentSubEmployerId,
    });
  };

  useEffect(() => {
    if (currentEmployerId) setCurrentEmployerSelected(currentEmployerId);
  }, [currentEmployerId]);

  useEffect(() => {
    setLabelHidden(!isSideBarCollapsed);
  }, [isSideBarCollapsed]);

  if (!showSidebar) return null;

  return (
    <>
      <SecondaryNavigationContainer
        isSideBarCollapsed={isSideBarCollapsed}
        isOpen={isOpen}
        onTransitionEnd={() => setLabelHidden(!isSideBarCollapsed)}
      >
        <div className="for-mobile">
          <MobileEmployerSwitcher
            currentEmployerSelected={currentEmployerSelected}
            employers={employers}
            handleClose={handleClose}
            onChange={onChange}
            isOpen={isOpen}
          />
        </div>
        <div className="for-desktop">
          <Logo isLabelHidden={!isLabelHidden} />
          <DesktopEmployerSwitcher
            employers={employers}
            onChange={desktopEmployerSwitcherOnChangeHandler}
            isLabelHidden={isLabelHidden}
            setMaskDisplayed={setMaskDisplayed}
          />
        </div>
        <SubNavLinkWrapper>
          {routesHandlerWithoutBetaPages.map(item => (
            <StyledLink
              isSelected={menuItemSelected === item.key}
              onClick={() => handleMenuItemClick(item)}
              key={item.key}
              isLabelHidden={isLabelHidden}
            >
              <NavIcon>
                <SVGIcon
                  svgChild={item.icon}
                  color={styles.color.textGrayColor}
                />
              </NavIcon>
              {(!isMobile) && (
                <>
                  <NavLink isLabelHidden={isLabelHidden}>
                    {item.title}
                  </NavLink>
                  <NavTooltip className="nav-tooltip">
                    {item.title}
                  </NavTooltip>
                </>
              )}
              {isMobile && (
              <NavLink>
                {item.title}
              </NavLink>
              )}
            </StyledLink>
          ))}
          {AMAZON_SPECIFIC_EMPLOYER_IDS.includes(currentEmployerId) && (
            <AmazonSpecificNavItems
              currentEmployerId={currentEmployerId}
              isLabelHidden={isLabelHidden}
            />
          )}
          {hasPartnerAccess && (
            <StyledLink
              href={config.partnerReportingUrl}
              target="_blank"
              isLabelHidden={isLabelHidden}
            >
              <NavIcon>
                <SVGIcon
                  svgChild={switchAccountIcon}
                  color={styles.color.jobcaseBlackColor}
                  size={24}
                />
              </NavIcon>
              {(!isMobile) && (
                <>
                  <NavLink isLabelHidden={isLabelHidden}>
                    Partner
                  </NavLink>
                  <NavTooltip className="nav-tooltip">
                    Partner
                  </NavTooltip>
                </>
              )}
              {isMobile && (
              <NavLink>
                Partner
              </NavLink>
              )}
            </StyledLink>
          )}
        </SubNavLinkWrapper>
        {!isMobile && (
          <>
            <ProfileMenu>
              <ProfileTooltipMenuWrapper isSideBarCollapsed={isSideBarCollapsed} isLabelHidden={isLabelHidden}>
                <ProfileTooltipMenu
                  imageSrc={currentProfile?.profile_photo || '/static-ec/profile-placeholder.png'}
                  menuItems={[{ onClick: handleOnClickViewProfile, text: 'View Profile' }, ...MENU_ITEMS]}
                  onOpen={val => setMaskDisplayed(val)}
                  onClose={val => setMaskDisplayed(val)}
                  title={fullName(currentProfile)}
                />
              </ProfileTooltipMenuWrapper>
            </ProfileMenu>
            <CollapseButton onClick={toggleCollapse} isSideBarCollapsed={isSideBarCollapsed}>
              <SVGIcon
                color={styles.color.jobcaseTrueBlackColor}
                svgChild={ChevronLeftLightSVG}
                size={12}
              />
            </CollapseButton>
          </>
        )}
      </SecondaryNavigationContainer>
      {isOpen && (
        <SideNavMask onClick={handleClose} data-fullstory-unmask />
      )}
      <Mask isMaskDisplayed={isMaskDisplayed} />
    </>
  );
};

SecondaryNavigation.propTypes = {
  currentEmployerSelected: PropTypes.string,
  employers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })),
  handleRoute: PropTypes.func,
  isOpen: PropTypes.bool,
  menuItemSelected: PropTypes.string,
  onChange: PropTypes.func,
  setCurrentEmployerSelected: PropTypes.func,
  showSidebar: PropTypes.bool,
  toggleOpen: PropTypes.func,
};

SecondaryNavigation.defaultProps = {
  currentEmployerSelected: null,
  employers: [],
  handleRoute: () => {},
  isOpen: false,
  menuItemSelected: null,
  onChange: () => {},
  setCurrentEmployerSelected: () => {},
  showSidebar: false,
  toggleOpen: () => {},
};

export default SecondaryNavigation;
